import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Clock,
  DollarSign,
  Package2,
  Users,
  Wrench,
  Activity,
  BarChart,
  Settings2,
  Building2,
  ListChecks,
  ClipboardCheck,
  MessageCircle,
  Globe,
  Calendar,
  CreditCard,
  MapPin,
  FileText,
  CheckCircle,
  UserCircle,
  FileBarChart2,
} from "lucide-react";
import { useBusiness } from "../contexts/BusinessContext";
import { supabase } from "../lib/supabase";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { useApplicationSettingsContext } from "../contexts/ApplicationSettingsContext";
import { PageId } from "../hooks/useApplicationSettings";
import { usePermissions } from "../contexts/PermissionContext";

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  text: string;
  badgeCount?: number;
  onClick?: () => void;
  showIndicator?: boolean;
}

function NavLink({ to, icon, text, badgeCount, onClick, showIndicator }: NavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`group flex items-center px-2 py-1.5 text-sm transition-all duration-200 rounded-xl ${
        isActive
          ? "bg-blue-50 text-blue-600"
          : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <div
        className={`flex items-center justify-center w-7 h-7 mr-2 rounded-full ${
          isActive 
            ? "bg-blue-100 text-blue-600 shadow-sm" 
            : "bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-700"
        } transition-all duration-200 relative`}
      >
        {icon}
        {showIndicator && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
        )}
      </div>
      <span className={`font-medium text-sm ${isActive ? "text-blue-600" : "text-gray-700 group-hover:text-gray-900"}`}>
        {text}
      </span>
      
      {/* Badge for unread count - only show when count is greater than 0 */}
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        <div className="ml-auto">
          <span className="flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-red-500 rounded-full">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        </div>
      )}
    </Link>
  );
}

interface LeftNavigationProps {
  hasSettings: boolean;
  isDevelopment?: boolean;
  timeclockPermissionLevel?: "manage" | "view" | "disabled";
  hasCalendar?: boolean;
  hasBookings?: boolean;
  proposalsAccess?: "manage" | "view" | "disabled";
  hasPayments?: boolean;
  hasCustomers?: boolean;
  hasMessages?: boolean;
  isMobile?: boolean;
  bottomNavItems?: string[];
  sidebarOpen?: boolean;
}

const ENABLE_TASKS = import.meta.env.VITE_ENABLE_TASKS === "true";

export default function LeftNavigation({
  hasSettings,
  isDevelopment = false,
  timeclockPermissionLevel,
  hasCalendar = false,
  hasBookings = false,
  proposalsAccess = "disabled",
  hasPayments = false,
  hasCustomers = false,
  hasMessages = false,
  isMobile = false,
  bottomNavItems = [],
  sidebarOpen = true,
}: LeftNavigationProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();

  const location = useLocation();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();

  // PWA Detection
  const [isPWA, setIsPWA] = useState(false);

  // Unread messages count state
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  useEffect(() => {
    // Detect PWA mode
    const detectPWA = () => {
      const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true ||
                       document.body.classList.contains('pwa-mode');
      setIsPWA(isPWAMode);
    };

    detectPWA();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', detectPWA);
    
    return () => mediaQuery.removeEventListener('change', detectPWA);
  }, []);

  // Fetch initial unread message count and set up realtime subscription
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Function to fetch current unread count
    const fetchUnreadCount = async () => {
      try {
        if (!supabase) {
          console.error('[NAV BADGE] Supabase client not initialized');
          return;
        }

        const { count, error } = await supabase
          .from('all_messages')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('read', 'unread')
          .eq('direction', 'inbound'); // Only count inbound unread messages

        if (error) {
          console.error('[NAV BADGE] Error fetching unread count:', error);
          return;
        }

        const newCount = count || 0;
        console.log('[NAV BADGE] 🔢 Updating badge count:', newCount);
        setUnreadMessagesCount(newCount);
      } catch (error) {
        console.error('[NAV BADGE] Error fetching unread count:', error);
      }
    };

    // Fetch initial count
    fetchUnreadCount();

    console.log('[NAV BADGE] 🔄 Setting up navigation badge subscription for business:', selectedBusiness.id);

    // Set up realtime subscription with more specific event handling
    const subscription = supabase
      .channel(`unread_messages_nav_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[NAV BADGE] 📨 Received database event:', {
            eventType: payload.eventType,
            messageId: (payload.new as any)?.id || (payload.old as any)?.id,
            direction: (payload.new as any)?.direction || (payload.old as any)?.direction,
            read: (payload.new as any)?.read || (payload.old as any)?.read
          });
          
          // Add a small delay to ensure database consistency
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe((status) => {
        console.log('[NAV BADGE] 📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('[NAV BADGE] ✅ Navigation badge subscription active');
        }
      });

    // Set up a periodic refresh as backup (every 30 seconds)
    const periodicRefresh = setInterval(() => {
      console.log('[NAV BADGE] 🔄 Periodic refresh of badge count');
      fetchUnreadCount();
    }, 30000);

    // Listen for custom events from Messages page for immediate updates
    const handleUnreadMessagesChanged = () => {
      console.log('[NAV BADGE] 📢 Received local update event, refreshing badge');
      fetchUnreadCount();
    };

    window.addEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[NAV BADGE] 🧹 Cleaning up navigation badge subscription');
      subscription.unsubscribe();
      clearInterval(periodicRefresh);
      window.removeEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);
    };
  }, [selectedBusiness?.id]);

  // Always show all navigation items in the left navigation (bottom nav has been removed)
  const shouldShowInLeftNav = (path: string) => {
    // Always show everything - bottom navigation has been removed
    return true;
  };

  // Helper to check if a page should be shown based on permissions and disabled status
  const shouldShowPage = (pageId: PageId, hasPermission: boolean) => {
    return hasPermission && !isPageDisabled(pageId);
  };

  return (
    <nav className={`flex flex-col h-full left-navigation p-2 ${
      isPWA && sidebarOpen ? 'pt-12' : isMobile ? 'pt-8' : ''
    } ${isPWA && sidebarOpen ? 'pwa-left-nav' : ''
    }`} style={{
      // Only apply PWA-specific styles when sidebar is open
      ...(isPWA && sidebarOpen ? {
        height: '100vh',
        minHeight: 'auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '280px', // Fixed sidebar width instead of 100%
        maxWidth: '280px',
        zIndex: 40,
        backgroundColor: '#eeeeef', // Ensure background color
        borderRight: '1px solid #e1e3e5' // Add border to define sidebar edge
      } : {
        // Regular styles when not PWA or sidebar closed
        height: isPWA ? 'auto' : 'auto',
        minHeight: isPWA ? 'auto' : '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? '80px' : '20px',
        position: isPWA ? 'relative' : 'sticky',
        top: isPWA ? 'auto' : 0,
        left: isPWA ? 'auto' : 'auto',
        width: isPWA ? 'auto' : 'auto',
        zIndex: isPWA ? 'auto' : 'auto'
      }),
      // Hide content when sidebar is closed
      display: sidebarOpen ? 'flex' : 'none'
    }}>
      {/* Only render content when sidebar is open */}
      {sidebarOpen && (
        <div className="flex flex-col h-full flex-grow w-full" style={{
          // Ensure content can scroll properly and stays within sidebar width
          minHeight: 'max-content',
          maxWidth: '100%',
          overflowX: 'hidden', // Prevent horizontal overflow
          overflowY: 'auto' // Allow vertical scrolling
        }}>
          <div className="flex-1 space-y-3 pr-1 w-full" style={{
            // Allow content to expand and scroll but constrain width
            flexShrink: 0,
            maxWidth: '100%'
          }}>
            {/* Profile/Business Display */}
            {selectedBusiness && (
              <div className={`w-full ${isPWA ? 'mt-2' : ''}`}>
                {currentResource && !currentResource.is_admin ? (
                  /* Resource Profile Card */
                  <Link 
                    to="/profile" 
                    className="w-full flex flex-col px-3 py-2 text-sm font-medium text-gray-900 bg-white rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white overflow-hidden">
                        {currentResource.avatar_url ? (
                          <img 
                            src={currentResource.avatar_url} 
                            alt={`${currentResource.first_name} ${currentResource.last_name}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          `${currentResource.first_name[0]}${currentResource.last_name[0]}`
                        )}
                      </div>
                      <div className="text-left overflow-hidden">
                        <div className="text-sm font-semibold truncate">
                          {currentResource.first_name} {currentResource.last_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {currentResource.roleName || currentResource.role || 'Team Member'}
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  /* Business Card for Admin */
                  <div className="w-full flex flex-col px-3 py-2 text-sm font-medium text-gray-900 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-full shadow-sm">
                        <Building2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="text-left overflow-hidden">
                        <div className="text-sm font-semibold truncate">
                          {selectedBusiness.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {selectedBusiness.address}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Top Navigation Links - Only visible on mobile */}
            {isMobile && (
              <div>
                <div className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Navigation
                </div>
                <div className="space-y-0.5">
                  {canAccess.kpiDashboard && (
                    <NavLink
                      to="/kpi-dashboard"
                      icon={<Activity className="h-4 w-4" />}
                      text="KPI Dashboard"
                    />
                  )}
                  
                  {shouldShowPage('calendar', hasCalendar) && (
                    <NavLink
                      to="/calendar"
                      icon={<Calendar className="h-4 w-4" />}
                      text="Calendar"
                    />
                  )}
                  
                  {canAccess.calendar && shouldShowPage('calendarV2', hasCalendar) && (
                    <NavLink
                      to="/calendarV2"
                      icon={<Calendar className="h-4 w-4" />}
                      text="Location Calendar"
                    />
                  )}
                  
                  {canAccess.calendar && shouldShowPage('mobile-calendar', hasCalendar) && (
                    <NavLink
                      to="/mobile-calendar"
                      icon={<Calendar className="h-4 w-4" />}
                      text="Mobile Calendar"
                    />
                  )}
                  
                  {shouldShowPage('bookings', hasBookings) && canAccess.bookings && (
                    <NavLink
                      to="/bookings"
                      icon={<FileBarChart2 className="h-4 w-4" />}
                      text="Bookings"
                    />
                  )}
                  
                  {shouldShowPage('proposals', proposalsAccess !== "disabled") && canAccess.proposals && (
                    <NavLink
                      to="/proposals"
                      icon={<FileText className="h-4 w-4" />}
                      text="Proposals"
                    />
                  )}
                  
                  {/* Payments page disabled temporarily - keeping code for future use */}
                  {/* {shouldShowPage('payments', hasPayments) && (
                    <NavLink
                      to="/payments"
                      icon={<CreditCard className="h-4 w-4" />}
                      text="Payments"
                    />
                  )} */}
                  
                  {shouldShowPage('customers', hasCustomers) && canAccess.customers && (
                    <NavLink
                      to="/customers"
                      icon={<Users className="h-4 w-4" />}
                      text="Customers"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Manage Section */}
            <div>
              <div className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Manage
              </div>
                            <div className="space-y-0.5">
              {/* Messages link - hidden on mobile as it's available as permanent FAB button */}
              {!isPageDisabled('messages') && canAccess.messages && !isMobile && (
                <NavLink
                  to="/messages"
                  icon={<MessageCircle className="h-4 w-4" />}
                  text="Messages"
                  badgeCount={unreadMessagesCount}
                />
              )}
              

              
              {/* CRM link - check if disabled and has permission */}
              {!isPageDisabled('crm') && canAccess.crm && (
                <NavLink
                  to="/crm"
                  icon={<Users className="h-4 w-4" />}
                  text="CRM"
                />
              )}
              
              {/* Payroll link - check if disabled and has permission */}
              {!isPageDisabled('payroll') && canAccess.payroll && (
                <NavLink
                  to="/payroll"
                  icon={<DollarSign className="h-4 w-4" />}
                  text="Payroll"
                />
              )}
              
              {/* Timeclock link */}
              {!isPageDisabled('timeclock') && canAccess.timeclock && (
                <NavLink
                  to="/timeclock"
                  icon={<Clock className="h-4 w-4" />}
                  text="Timeclock"
                />
              )}
              
              {/* Stock/Supplies link */}
              {!isPageDisabled('supplies') && canAccess.supplies && (
                <NavLink
                  to="/stock"
                  icon={<Package2 className="h-4 w-4" />}
                  text="Supplies"
                />
              )}
              
              {/* Services link */}
              {!isPageDisabled('services') && canAccess.services && (
                <NavLink
                  to="/services"
                  icon={<Wrench className="h-4 w-4" />}
                  text="Services"
                />
              )}
              
              {/* Resources link */}
              {!isPageDisabled('resources') && canAccess.resources && (
                <NavLink
                  to="/resources"
                  icon={<Users className="h-4 w-4" />}
                  text="Resources"
                />
              )}
              
              {/* Automations link */}
              {!isPageDisabled('automations') && canAccess.automations && (
                <NavLink
                  to="/automations"
                  icon={<Activity className="h-4 w-4" />}
                  text="Automations"
                />
              )}
              
              {/* Automations v2 link */}
              {!isPageDisabled('automations') && canAccess.automations && (
                <NavLink
                  to="/automations-v2"
                  icon={<Activity className="h-4 w-4" />}
                  text="Automations v2"
                />
              )}
              
              {/* Tasks link */}
              {!isPageDisabled('tasks') && canAccess.tasks && (
                <NavLink
                  to="/tasks"
                  icon={<ListChecks className="h-4 w-4" />}
                  text="Tasks"
                />
              )}
              

              </div>
            </div>

            {/* Reports Section */}
            {!isPageDisabled('sales-reports') && canAccess.salesReports && (
              <div>
                <div className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Reports
                </div>
                <div className="space-y-0.5">
                {/* Sales Reports link */}
                  <NavLink
                    to="/sales-reports"
                    icon={<BarChart className="h-4 w-4" />}
                    text="Sales Reports"
                  />
                {/* Team Reports link */}
                {!isPageDisabled('team-reports') && canAccess.teamReports && (
                  <NavLink
                    to="/team-reports"
                    icon={<Users className="h-4 w-4" />}
                    text="Team Reports"
                  />
                )}
                {/* Supplies Usage Report link */}
                {!isPageDisabled('supplies-usage') && canAccess.suppliesUsage && (
                  <NavLink
                    to="/supplies-usage"
                    icon={<Package2 className="h-4 w-4" />}
                    text="Supplies Usage"
                  />
                )}
                </div>
              </div>
            )}

            {/* Settings Link */}
            {shouldShowPage('settings', hasSettings) && canAccess.settings && (
              <div className="pt-3 mt-3 border-t border-gray-100">
                <div className="space-y-0.5">
                {/* Settings link */}
                <NavLink
                  to="/settings"
                  icon={<Settings2 className="h-4 w-4" />}
                  text="Settings"
                />
                </div>
              </div>
            )}
            
            {/* Add extra padding space at bottom when on mobile to ensure everything is accessible */}
            {isMobile && !isPWA && <div className="h-16"></div>}
          </div>
        </div>
      )}
    </nav>
  );
}
